// S2 — Detalle de línea (drill-down desde S1).
//
// 🚧 PENDIENTE refactor visual a detalle (sesión 2026-05-28).
// Ajuste mínimo aplicado para coherencia con el canon (S1/S5/S6, C1/C2/C3/C5/C6):
//   - Header consistente (título + subtítulo + botones top-right con variant=outline size=sm)
//   - Tokens semánticos (text-fg / text-fg-tertiary / text-fg-secondary / text-crit/warn/ok)
//   - Loading/error states alineados
//   - Sin colores hardcoded (text-red-XXX, bg-gray-XXX, etc.)
//
// NO se agregó lógica nueva, KPI cards con Sparkline ni gráfico Recharts —
// eso queda para la revisión a detalle por pantalla con HTML+PNG del diseño.
//
// Acción principal del rol: validar las capturas del líder hora por hora,
// o invalidarlas con razón ≥20 chars.
//
// Ventana 30min del líder: NO bloquea al supervisor (decisión MVP — autoridad
// jerárquica). El líder puede editar lo suyo dentro de 30min; el supervisor
// puede invalidar siempre.

import { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import type { BloqueTurno, RegistroProduccionHora, SupervisorBloque } from 'schemas';
import { generarBloquesTurno } from 'schemas';
import { EditarHoraDialog } from '../captura/EditarHoraDialog';
import {
  agruparPorCelda,
  cantidadSkusDistintos,
  cantidadTotalGrupo,
  cumplimientoGrupoPct,
  formatearMiles,
} from '../captura/shared';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Spinner } from '../../components/ui/spinner';
import { Textarea } from '../../components/ui/textarea';
import { RoleLayout } from '../../components/layout/RoleLayout';
import { SupervisorSidebar } from '../../components/layout/SupervisorSidebar';
import { ApiError } from '../../lib/api/client';
import {
  useInvalidarRegistro,
  useSupervisorLineaDetalle,
  useValidarRegistro,
} from '../../lib/api/queries';
import { useAuth } from '../../lib/auth';
import { identidadUsuario } from '../../lib/identidad';
import { ParosBloqueDialog } from './ParosBloqueDialog';

export function S2DetalleLineaPage() {
  const { lineaId } = useParams<{ lineaId: string }>();
  const { state, signOut } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useSupervisorLineaDetalle(lineaId);
  const validarMut = useValidarRegistro();
  const invalidarMut = useInvalidarRegistro();

  // Tipo mínimo para el modal: solo lo que la UI muestra. Evita mismatch del
  // tipo inferido entre input/output de Zod (`.default()` cambia los tipos).
  type ModalRegistro = {
    id: string;
    hora_inicio_bloque: string;
    cantidad_procesada: number;
    numero_operarios: number;
  };
  const [invalidarModal, setInvalidarModal] = useState<ModalRegistro | null>(null);
  const [razon, setRazon] = useState('');
  // Edición del supervisor (motivo ≥20) sobre cualquier bloque.
  const [editarRegistro, setEditarRegistro] = useState<RegistroProduccionHora | null>(null);
  // Detalle de los paros de un bloque (confirmar / corregir).
  const [parosBloque, setParosBloque] = useState<RegistroProduccionHora | null>(null);

  // Agrupación visual por celda (multi-sku-por-hora, Lote 5): SOLO
  // presentación — reusa el mismo helper de "Mis horas" (captura/shared.tsx).
  // La validación/invalidación/edición sigue actuando POR REGISTRO (`id`),
  // sin cambios de contrato: cada `BloqueRow` dentro de un grupo conserva su
  // propia acción individual. Con turno sin horas resueltas (`fuente:
  // 'sin_turno'`) no hay `BloqueTurno[]` para agrupar — se muestra 1 grupo
  // por bloque (comportamiento actual, sin romper).
  const bloquesTurnoObjetivo: BloqueTurno[] =
    data?.turno_objetivo.hora_inicio && data.turno_objetivo.hora_fin
      ? generarBloquesTurno({
          hora_inicio: data.turno_objetivo.hora_inicio,
          hora_fin: data.turno_objetivo.hora_fin,
          cruza_medianoche: data.turno_objetivo.cruza_medianoche,
        })
      : [];
  const gruposBloques = useMemo(() => {
    const bloquesData = data?.bloques ?? [];
    if (bloquesTurnoObjetivo.length === 0) {
      return bloquesData.map((b) => ({
        celda: {
          inicio: String(b.registro.hora_inicio_bloque).slice(0, 5),
          fin: String(b.registro.hora_fin_bloque).slice(0, 5),
        },
        items: [b],
      }));
    }
    const wrapperPorId = new Map(bloquesData.map((b) => [b.registro.id, b]));
    return agruparPorCelda(
      bloquesData.map((b) => b.registro),
      bloquesTurnoObjetivo,
    ).map((g) => ({
      celda: g.celda,
      items: g.tramos.map((r) => wrapperPorId.get(r.id)!),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.bloques, bloquesTurnoObjetivo.length]);

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  if (isLoading) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 bg-surface-base p-6 text-fg">
        <Spinner className="size-6" />
        <p className="text-sm text-fg-secondary">Cargando detalle…</p>
      </main>
    );
  }

  if (isError || !data) {
    const isForbidden = error instanceof ApiError && error.status === 403;
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 bg-surface-base p-6 text-center text-fg">
        <h1 className="text-lg font-medium text-crit">
          {isForbidden ? 'Línea fuera de su scope' : 'No pudimos cargar el detalle'}
        </h1>
        <p className="text-sm text-fg-secondary">
          {error instanceof Error ? error.message : 'Error desconocido'}
        </p>
        <Button onClick={() => navigate('/supervisor')}>Volver al dashboard</Button>
      </main>
    );
  }

  function handleValidar(registroId: string) {
    validarMut.mutate(registroId, {
      onSuccess: () => toast.success('Registro validado'),
      onError: (err) => {
        if (err instanceof ApiError) {
          toast.error(err.message);
        } else {
          toast.error('Error al validar');
        }
      },
    });
  }

  function handleInvalidar() {
    if (!invalidarModal) return;
    if (razon.trim().length < 20) {
      toast.error('La razón debe tener al menos 20 caracteres');
      return;
    }
    invalidarMut.mutate(
      { registroId: invalidarModal.id, razon: razon.trim() },
      {
        onSuccess: () => {
          toast.success('Registro invalidado');
          setInvalidarModal(null);
          setRazon('');
        },
        onError: (err) => {
          if (err instanceof ApiError) {
            toast.error(err.message);
          } else {
            toast.error('Error al invalidar');
          }
        },
      },
    );
  }

  const { linea, turno_objetivo, programacion_lider_hoy, kpi_linea, bloques } = data;
  // Botón "Capturar por delegación" se muestra solo si hay líder asignado +
  // turno activo. Backend además valida scope (planta∈scope del supervisor).
  const puedeDelegar = programacion_lider_hoy.activa && turno_objetivo.id !== null;

  return (
    <RoleLayout sidebar={<SupervisorSidebar />} roleLabel="Supervisor">
      <div className="flex flex-col gap-4">
        <header className="flex items-baseline justify-between gap-2 border-b border-border pb-3">
          <div>
            <Link to="/supervisor" className="text-[11px] text-fg-tertiary hover:text-fg-secondary">
              ← Volver al dashboard
            </Link>
            <h1 className="text-xl font-medium text-fg">
              {linea.nombre} <span className="text-base text-fg-tertiary">{linea.codigo}</span>
            </h1>
            <p className="mt-1 text-[11px] text-fg-tertiary">
              {linea.planta.nombre} · {linea.area_nombre} ·{' '}
              {turno_objetivo.numero !== null ? (
                <>
                  Turno {turno_objetivo.numero}
                  <span className="text-fg-tertiary"> ({data.hora_servidor})</span>
                </>
              ) : (
                <span className="text-fg-tertiary">Fuera de turno</span>
              )}
            </p>
          </div>
          {puedeDelegar ? (
            <Link
              to={`/supervisor/linea/${linea.id}/capturar`}
              className="inline-flex items-center gap-1 rounded border border-warn bg-warn-soft px-3 py-1.5 text-xs font-medium text-warn hover:opacity-90"
            >
              Capturar por delegación
            </Link>
          ) : null}
        </header>

        {/* Líder + KPI agregado */}
        <section className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Card>
            <CardHeader>
              <h2 className="text-xs font-semibold uppercase tracking-wider text-fg-tertiary">
                Líder asignado
              </h2>
            </CardHeader>
            <CardContent>
              {programacion_lider_hoy.lider ? (
                <div>
                  <p className="text-base font-medium">
                    {programacion_lider_hoy.lider.nombre} {programacion_lider_hoy.lider.apellido}
                  </p>
                  <p className="text-xs text-fg-tertiary">
                    {identidadUsuario(programacion_lider_hoy.lider.email).etiqueta}
                  </p>
                  {programacion_lider_hoy.es_linea_principal === false ? (
                    <p className="mt-1 text-xs text-warn">Línea secundaria</p>
                  ) : null}
                </div>
              ) : (
                <p className="text-sm text-fg-tertiary">Sin líder asignado para este turno</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <h2 className="text-xs font-semibold uppercase tracking-wider text-fg-tertiary">
                KPI del turno
              </h2>
            </CardHeader>
            <CardContent>
              {kpi_linea ? (
                <div className="space-y-1 text-sm">
                  <p>
                    <strong className="text-lg">{kpi_linea.cantidad_real}</strong> u en{' '}
                    {kpi_linea.bloques_capturados} bloque(s)
                  </p>
                  {kpi_linea.cumplimiento_pct !== null ? (
                    <p className="text-xs text-fg-secondary">
                      Cumplimiento: <strong>{kpi_linea.cumplimiento_pct}%</strong> (meta{' '}
                      {kpi_linea.meta_acumulada})
                    </p>
                  ) : (
                    <p className="text-xs text-warn">⚠ Falta capacidad estándar</p>
                  )}
                  {kpi_linea.paros_count > 0 ? (
                    <p className="text-xs text-fg-secondary">
                      {kpi_linea.paros_count} paro(s) · {kpi_linea.paros_min_totales} min perdidos
                      {kpi_linea.paro_activo ? (
                        <span className="ml-1 font-semibold text-crit">· activo</span>
                      ) : null}
                    </p>
                  ) : null}
                </div>
              ) : (
                <p className="text-sm text-fg-tertiary">Sin captura en este turno</p>
              )}
            </CardContent>
          </Card>
        </section>

        {/* Lista de bloques */}
        <section>
          <h2 className="mb-2 text-sm font-semibold text-fg-secondary">
            Bloques del turno ({gruposBloques.length})
          </h2>
          {bloques.length === 0 ? (
            <Card>
              <CardContent className="py-6 text-center text-sm text-fg-tertiary">
                Sin bloques capturados en este turno todavía.
              </CardContent>
            </Card>
          ) : (
            <div className="flex flex-col gap-2">
              {gruposBloques.map((g) =>
                g.items.length === 1 ? (
                  <BloqueRow
                    key={g.items[0]!.registro.id}
                    bloque={g.items[0]!}
                    onValidar={() => handleValidar(g.items[0]!.registro.id)}
                    onInvalidar={() => setInvalidarModal(g.items[0]!.registro)}
                    onEditar={() => setEditarRegistro(g.items[0]!.registro)}
                    onVerParos={() => setParosBloque(g.items[0]!.registro)}
                    isValidating={
                      validarMut.isPending && validarMut.variables === g.items[0]!.registro.id
                    }
                  />
                ) : (
                  <div
                    key={g.celda.inicio}
                    className="flex flex-col gap-2 rounded border border-border bg-surface-overlay/30 p-2"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2 px-1">
                      <span className="text-xs font-semibold text-fg-secondary">
                        {g.celda.inicio}–{g.celda.fin}
                      </span>
                      <div className="flex items-center gap-2">
                        <Badge tone="info">
                          {cantidadSkusDistintos(g.items.map((i) => i.registro))} SKUs ·{' '}
                          {g.items.length} tramos
                        </Badge>
                        {(() => {
                          const registros = g.items.map((i) => i.registro);
                          const total = cantidadTotalGrupo(registros);
                          const pct = cumplimientoGrupoPct(registros);
                          return (
                            <span className="text-xs text-fg-tertiary">
                              {formatearMiles(total)} u{pct !== null ? ` · ${pct}%` : ''}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      {g.items.map((b) => (
                        <BloqueRow
                          key={b.registro.id}
                          bloque={b}
                          onValidar={() => handleValidar(b.registro.id)}
                          onInvalidar={() => setInvalidarModal(b.registro)}
                          onEditar={() => setEditarRegistro(b.registro)}
                          onVerParos={() => setParosBloque(b.registro)}
                          isValidating={
                            validarMut.isPending && validarMut.variables === b.registro.id
                          }
                        />
                      ))}
                    </div>
                  </div>
                ),
              )}
            </div>
          )}
        </section>

        {/* Modal invalidar */}
        <Dialog
          open={invalidarModal !== null}
          onClose={() => {
            setInvalidarModal(null);
            setRazon('');
          }}
        >
          <DialogHeader>
            <DialogTitle>Invalidar registro</DialogTitle>
          </DialogHeader>
          <DialogContent>
            {invalidarModal ? (
              <div className="space-y-3">
                <p className="text-sm text-fg-secondary">
                  Bloque <strong>{String(invalidarModal.hora_inicio_bloque).slice(0, 5)}</strong> ·{' '}
                  {invalidarModal.cantidad_procesada} u · {invalidarModal.numero_operarios} ops
                </p>
                <p className="text-xs text-warn">
                  Esto marca el registro como inválido (soft delete). El líder verá el cambio en su
                  vista L3. La acción queda auditada.
                </p>
                <div>
                  <Label htmlFor="razon">Razón (mínimo 20 caracteres)</Label>
                  <Textarea
                    id="razon"
                    value={razon}
                    onChange={(e) => setRazon(e.target.value)}
                    placeholder="Ej: cantidad capturada no coincide con conteo físico final"
                    className="mt-1"
                  />
                  <p
                    className={`mt-1 text-xs ${razon.trim().length >= 20 ? 'text-ok' : 'text-fg-tertiary'}`}
                  >
                    {razon.trim().length}/20 caracteres mínimos
                  </p>
                </div>
              </div>
            ) : null}
          </DialogContent>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setInvalidarModal(null);
                setRazon('');
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              disabled={razon.trim().length < 20 || invalidarMut.isPending}
              onClick={handleInvalidar}
            >
              {invalidarMut.isPending ? 'Invalidando…' : 'Invalidar'}
            </Button>
          </DialogFooter>
        </Dialog>

        {/* Detalle de paros del bloque: confirmar o corregir (mig 079) */}
        {parosBloque ? (
          <ParosBloqueDialog
            bloqueLabel={`${String(parosBloque.hora_inicio_bloque).slice(0, 5)}–${String(
              parosBloque.hora_fin_bloque,
            ).slice(0, 5)}`}
            paros={parosBloque.paros ?? []}
            detalle={data.paros_detalle}
            onClose={() => setParosBloque(null)}
          />
        ) : null}

        {/* Edición del supervisor (motivo ≥20) sobre cualquier bloque */}
        <EditarHoraDialog
          registro={editarRegistro}
          requiereRazon
          hint="Edición del supervisor. Indique el motivo del cambio (queda auditado)."
          onClose={() => setEditarRegistro(null)}
        />
      </div>
    </RoleLayout>
  );
}

// =============================================================================
// BloqueRow
// =============================================================================

function BloqueRow({
  bloque,
  onValidar,
  onInvalidar,
  onEditar,
  onVerParos,
  isValidating,
}: {
  bloque: SupervisorBloque;
  onValidar: () => void;
  onInvalidar: () => void;
  onEditar: () => void;
  onVerParos: () => void;
  isValidating: boolean;
}) {
  const r = bloque.registro;
  const horaIni = String(r.hora_inicio_bloque).slice(0, 5);
  const horaFin = String(r.hora_fin_bloque).slice(0, 5);

  const invalidado = r.estado === 'invalidado';
  const validado = bloque.es_validado;

  return (
    <Card
      className={
        invalidado
          ? 'border-border bg-surface-overlay opacity-70'
          : validado
            ? 'border-ok bg-ok-soft/30'
            : 'border-border'
      }
    >
      <CardContent className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm font-semibold">
              {horaIni}–{horaFin}
            </span>
            {validado ? (
              <span className="rounded bg-ok-soft px-1.5 py-0.5 text-[10px] font-semibold text-ok">
                ✓ Validado
              </span>
            ) : invalidado ? (
              <span className="rounded bg-surface-overlay px-1.5 py-0.5 text-[10px] font-semibold text-fg-tertiary">
                Invalidado
              </span>
            ) : (
              <span className="rounded bg-warn-soft px-1.5 py-0.5 text-[10px] font-semibold text-warn">
                Pendiente
              </span>
            )}
          </div>
          <p className="text-sm">
            <strong>{r.cantidad_procesada}</strong> u · {r.numero_operarios} ops
            {r.golpes_por_minuto ? ` · ${r.golpes_por_minuto} gpm` : ''}
          </p>
          {r.op_lote_oc_cliente ? (
            <p className="text-xs text-fg-tertiary">OP: {r.op_lote_oc_cliente}</p>
          ) : null}
          {(r.paros ?? []).length > 0 ? (
            <button
              type="button"
              onClick={onVerParos}
              className="w-fit text-xs text-fg-secondary underline decoration-dotted underline-offset-2 hover:text-fg"
            >
              ⚠ {(r.paros ?? []).length} paro(s) · ver detalle
            </button>
          ) : null}
          {invalidado && r.razon_invalidacion ? (
            <p className="text-xs italic text-fg-secondary">Razón: {r.razon_invalidacion}</p>
          ) : null}
        </div>

        {!invalidado ? (
          <div className="flex flex-wrap gap-2">
            {!validado ? (
              <Button size="sm" onClick={onValidar} disabled={isValidating}>
                {isValidating ? '...' : 'Validar'}
              </Button>
            ) : null}
            <Button size="sm" variant="outline" onClick={onEditar}>
              Editar
            </Button>
            {bloque.puede_invalidar ? (
              <Button size="sm" variant="outline" onClick={onInvalidar}>
                Invalidar
              </Button>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
